import type { ChangeEvent } from 'react';
import useAuthMetamaskUser from '~/client/features/Metamask/hooks/useAuthMetamaskUser';
import useTranslation from '~/client/hooks/i18n/useTranslation';
import useQueryParam from '~/client/hooks/router/useQueryParam';
import useSite from '~/client/hooks/useSite';
import dateObjectFromDateString from '~/client/shared/form/FormDateInput/DateInput/useDateInput/helpers/dateObjectFromDateString';
import formatDateToMonthDayYear from '~/client/shared/form/FormDateInput/DateInput/useDateInput/helpers/formatDateTime';
import minimumYear from '~/client/shared/form/FormDateInput/DateInput/useDateInput/helpers/minimumYear';
import useTermsUrl from '~/client/shared/TermsAndConditions/useTermsUrl';
import SessionStorage from '~/shared/utils/storage/SessionStorage';
import useLoginOrSignupSubmit from './helpers/useLoginOrSignupSubmit';
import useFormRegister from '../../../Register/RegisterBody/FormRegister/useFormRegister';
import useStepByStepAuthContext from '../context/hooks/useStepByStepAuthContext';

type Props = {
  isPopover?: boolean;
  onSuccess?: () => void;
};

const useFinishSignupStep = ({ onSuccess, isPopover }: Props) => {
  const {
    isEmail,
    getValues: formValues,
    setValue,
    formState,
    errors: formErrors,
  } = useStepByStepAuthContext();
  const { socialAuthType } = useLoginOrSignupSubmit({});

  const [isMetamask] = useQueryParam('metamask');
  const [isTonConnect] = useQueryParam('tonconnect');
  const isWalletAuthConnection = !!isMetamask || !!isTonConnect;
  const missingFields = SessionStorage.getItem(
    'social_auth_missing_fields',
  ) as string;
  const isEmailRequired =
    (missingFields?.split(',')?.includes('email') && !!socialAuthType) ||
    isWalletAuthConnection;
  const email = isEmail ? formValues?.('username') : '';
  const username = isEmail ? '' : formValues?.('username');
  const { loading: loadingMetamask } = useAuthMetamaskUser();

  const defaultValues = {
    offers: false,
    acceptsTerms: false,
    email,
    username,
  };

  const {
    handleSubmit,
    loading,
    isCaptchaRequired,
    control,
    errors,
    required,
    recaptchaRef,
    callingCodes,
    isSubmitDisabled,
    optional,
    setError,
    setValue: setFormValue,
  } = useFormRegister({
    onSuccess,
    isPopover,
    defaultValues,
  });

  const { siteConfig } = useSite();
  const termsLink = useTermsUrl();

  const offersEmailLabel = useTranslation('profile.email');
  const offersSmsLabel = useTranslation('profile.sms');
  const offersCallLabel = useTranslation('profile.call');
  const offersLabel = {
    offersEmailLabel,
    offersSmsLabel,
    offersCallLabel,
  };

  const termsLabel = useTranslation(
    'profile.signup.i_agree_terms_and_privacy',
    {
      values: {
        terms_url: termsLink,
        privacy_url: siteConfig.privacyPolicyLink,
      },
    },
  );

  const currentDate = new Date();
  const { day, month, year } = formatDateToMonthDayYear(currentDate);
  const selectedDate = `${year}-${month}-${day}`;

  const handleDateOfBirthchange = (event: ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();
    const { value } = event.target;
    const { isValid } = minimumYear(value);

    if (!isValid) {
      setValue?.('dateOfBirth', selectedDate, {
        shouldValidate: true,
      });
    }
    return setValue?.('dateOfBirth', value, {
      shouldValidate: true,
      shouldDirty: !!errors?.dateOfBirth?.day,
    });
  };

  const handleBlur = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    if (!value) {
      return setError('dateOfBirth.day', { message: 'errors.INVALID_DATE' });
    }
    const defaultObj = dateObjectFromDateString(value ?? selectedDate);
    const { isValid, year: minYear, currentYear } = minimumYear(value);
    if (minYear > currentYear) {
      setFormValue('dateOfBirth', defaultObj, {
        shouldValidate: true,
        shouldDirty: true,
      });
      return setError('dateOfBirth.day', { message: 'errors.INVALID_DATE' });
    }
    if (!isValid) {
      return setError('dateOfBirth.day', {
        message: 'errors.AGE_MUST_BE_OVER_18',
      });
    }
    setError('dateOfBirth.day', { message: '' });
    return setFormValue('dateOfBirth', defaultObj, {
      shouldValidate: true,
    });
  };

  return {
    control,
    formErrors,
    formState,
    isEmailRequired,
    isWalletAuthConnection,
    handleBlur,
    socialAuthType,
    offersEmailLabel,
    offersLabel,
    offersSmsLabel,
    offersCallLabel,
    loading: loading || loadingMetamask,
    optional,
    errors,
    isCaptchaRequired,
    recaptchaRef,
    callingCodes,
    isSubmitDisabled,
    required,
    handleSubmit,
    termsLabel,
    username,
    email,
    isEmail,
    handleDateOfBirthchange,
    inputSize: 'xlarge',
  };
};

export default useFinishSignupStep;
